/**
 * 新建 / 编辑主题弹窗（旧 views/themes/components/ThemeModal.vue）。
 * 封面和角色头像先上传拿 URL，再随主题一起保存；两个 JSON 字段用代码编辑器输入。
 */
import { useState } from 'react'
import { App, Button, Col, Divider, Form, Input, Modal, Row, Select, Space, Switch, Upload, type UploadProps } from 'antd'
import { PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { CodeEditor } from '@/components/code-editor/CodeEditor'
import { MarkdownVisualEditor } from '@/components/MarkdownVisualEditor'
import { normalizeMarkdownEmphasisSpacing, stripMarkdownMarkers } from '@/utils/markdownText'
import {
  resolveThemeAssetUrl,
  themesApi,
  THEME_CEFR_LEVELS,
  type ThemeCEFRLevel,
  type ThemeCharacterCard,
  type ThemeSavePayload
} from '@/api/services/themes'
import { CATEGORY_OPTIONS, LOCALE_OPTIONS } from './constants'
import { useSaveTheme } from './hooks'
import { themeTags, type Theme } from './types'
import styles from './ThemeModal.module.css'

interface ThemeFormValues {
  code: string
  title: string
  description: string
  category: string
  cefr_level?: ThemeCEFRLevel
  locale: string
  is_active: boolean
  list_hook: string
  tags: string[]
  first_episode_title: string
  first_episode_story_text: string
  theme_specific_prompt: string
}

interface CharacterCardDraft {
  uid: string
  avatar_url: string
  name: string
  role: string
}

const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif'])
const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif'
const MAX_IMAGE_BYTES = 10 * 1024 * 1024

const makeCardUid = () => `card-${crypto.randomUUID()}`

function toCardDrafts(cards: unknown): CharacterCardDraft[] {
  if (!Array.isArray(cards)) return []
  return cards.map((card) => {
    const item = card && typeof card === 'object' ? (card as Record<string, unknown>) : {}
    return {
      uid: makeCardUid(),
      avatar_url: typeof item.avatar_url === 'string' ? item.avatar_url : '',
      name: typeof item.name === 'string' ? item.name : '',
      role: typeof item.role === 'string' ? item.role : ''
    }
  })
}

function toJsonText(value: unknown) {
  return typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2)
}

function initialValues(theme: Theme | null): ThemeFormValues {
  return {
    code: theme?.code || '',
    title: stripMarkdownMarkers(theme?.title || ''),
    description: theme?.description || '',
    category: theme?.category || 'slice_of_life',
    cefr_level: theme?.cefr_level,
    locale: theme?.locale || 'en-US',
    is_active: theme?.is_active ?? true,
    list_hook: theme?.list_hook || '',
    tags: themeTags(theme),
    first_episode_title: stripMarkdownMarkers(theme?.first_episode_title || ''),
    first_episode_story_text: theme?.first_episode_story_text || '',
    theme_specific_prompt: theme?.theme_specific_prompt || ''
  }
}

const toNullableText = (value: string) => value.trim() || null

function isAllowedImage(file: File) {
  const suffix = `.${file.name.split('.').pop()?.toLowerCase() || ''}`
  return file.type.startsWith('image/') && IMAGE_EXTENSIONS.has(suffix)
}

function uploadErrorMessage(error: any, fallback: string) {
  const data = error?.response?.data
  if (typeof data?.detail === 'string' && data.detail) return data.detail
  if (typeof data?.message === 'string' && data.message) return data.message
  return error instanceof Error && error.message ? error.message : fallback
}

interface ThemeFormProps {
  theme: Theme | null
  onClose: () => void
}

function ThemeForm({ theme, onClose }: Readonly<ThemeFormProps>) {
  const { message } = App.useApp()
  const [form] = Form.useForm<ThemeFormValues>()
  const saveTheme = useSaveTheme()
  const isEdit = theme !== null

  const [coverUrl, setCoverUrl] = useState(theme?.cover_image_url || '')
  const [cards, setCards] = useState<CharacterCardDraft[]>(() => toCardDrafts(theme?.character_cards))
  const [fixedSettingJson, setFixedSettingJson] = useState(() => toJsonText(theme?.initial_fixed_setting))
  const [styleHintsJson, setStyleHintsJson] = useState(() => toJsonText(theme?.style_hints))
  const [uploadingCover, setUploadingCover] = useState(false)
  // 正在上传头像的角色卡；几张卡可以同时上传，全部完成前不能保存
  const [uploadingCardUids, setUploadingCardUids] = useState<ReadonlySet<string>>(new Set())
  const isUploading = uploadingCover || uploadingCardUids.size > 0

  const updateCard = (uid: string, patch: Partial<CharacterCardDraft>) =>
    setCards((current) => current.map((card) => (card.uid === uid ? { ...card, ...patch } : card)))

  const removeCard = (uid: string) => setCards((current) => current.filter((item) => item.uid !== uid))

  const moveCard = (index: number, direction: -1 | 1) =>
    setCards((current) => {
      const next = [...current]
      const [card] = next.splice(index, 1)
      next.splice(index + direction, 0, card)
      return next
    })

  // 校验图片后异步上传；返回 false 阻止 antd Upload 自己提交
  const imageUpload = (upload: (file: File) => Promise<void>): UploadProps['beforeUpload'] => (file) => {
    if (!isAllowedImage(file)) {
      message.error('仅支持 jpg / jpeg / png / gif')
      return false
    }
    if (file.size > MAX_IMAGE_BYTES) {
      message.error('文件大小不能超过 10MB')
      return false
    }
    void upload(file)
    return false
  }

  const uploadCover = imageUpload(async (file) => {
    setUploadingCover(true)
    try {
      setCoverUrl(await themesApi.uploadCover(file))
      message.success('封面已上传')
    } catch (error) {
      message.error(uploadErrorMessage(error, '封面上传失败'))
    } finally {
      setUploadingCover(false)
    }
  })

  const uploadAvatar = (uid: string) =>
    imageUpload(async (file) => {
      setUploadingCardUids((current) => new Set(current).add(uid))
      try {
        updateCard(uid, { avatar_url: await themesApi.uploadCharacter(file) })
        message.success('角色头像已上传')
      } catch (error) {
        message.error(uploadErrorMessage(error, '角色头像上传失败'))
      } finally {
        setUploadingCardUids((current) => {
          const next = new Set(current)
          next.delete(uid)
          return next
        })
      }
    })

  const handleSubmit = async () => {
    if (isUploading) {
      message.warning('图片仍在上传，请稍后再保存')
      return
    }
    const values = await form.validateFields().catch(() => null)
    if (!values) return

    let initialFixedSetting: Record<string, unknown>
    let styleHints: Record<string, unknown>
    try {
      initialFixedSetting = JSON.parse(fixedSettingJson || '{}')
    } catch {
      message.error('初始固定设定JSON格式错误')
      return
    }
    try {
      styleHints = JSON.parse(styleHintsJson || '{}')
    } catch {
      message.error('风格提示JSON格式错误')
      return
    }

    const characterCards: ThemeCharacterCard[] = cards
      .map((card) => ({
        avatar_url: toNullableText(card.avatar_url),
        name: toNullableText(card.name),
        role: toNullableText(card.role)
      }))
      .filter((card) => card.avatar_url || card.name || card.role)

    const payload: ThemeSavePayload = {
      code: values.code,
      title: stripMarkdownMarkers(values.title),
      description: normalizeMarkdownEmphasisSpacing(values.description || ''),
      category: values.category,
      cefr_level: values.cefr_level!,
      locale: values.locale,
      is_active: values.is_active,
      initial_fixed_setting: initialFixedSetting,
      first_episode_title: stripMarkdownMarkers(values.first_episode_title || ''),
      first_episode_story_text: normalizeMarkdownEmphasisSpacing(values.first_episode_story_text || ''),
      style_hints: styleHints,
      theme_specific_prompt: values.theme_specific_prompt || '',
      cover_image_url: toNullableText(coverUrl),
      list_hook: toNullableText(values.list_hook || ''),
      character_cards: characterCards,
      tags: values.tags
    }

    try {
      await saveTheme.mutateAsync({ id: theme?.id, payload })
      message.success(isEdit ? '主题更新成功' : '主题创建成功')
      onClose()
    } catch (error) {
      // 失败时弹窗保持打开，已填内容不丢
      console.error('Submit error:', error)
      message.error(isEdit ? '更新失败' : '创建失败')
    }
  }

  // 失焦时去掉标题里残留的 Markdown 标记
  const stripOnBlur = (field: 'title' | 'first_episode_title') => () =>
    form.setFieldValue(field, stripMarkdownMarkers(form.getFieldValue(field) || ''))

  return (
    <Modal
      open
      title={isEdit ? '编辑主题' : '创建主题'}
      width={880}
      confirmLoading={saveTheme.isPending || isUploading}
      okButtonProps={{ disabled: isUploading }}
      onOk={() => void handleSubmit()}
      onCancel={onClose}
    >
      <Form<ThemeFormValues> form={form} layout="vertical" initialValues={initialValues(theme)}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="主题代码"
              name="code"
              rules={[
                { required: true, message: '请输入主题代码' },
                { min: 1, max: 100, message: '代码长度为1-100个字符' }
              ]}
            >
              <Input placeholder="例如: max_the_dog" disabled={isEdit} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="主题标题"
              name="title"
              rules={[
                { required: true, message: '请输入主题标题' },
                { min: 1, max: 200, message: '标题长度为1-200个字符' }
              ]}
            >
              <Input placeholder="例如: Max the Dog's Day" onBlur={stripOnBlur('title')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="主题描述" name="description">
          <MarkdownVisualEditor placeholder="描述主题的内容和适用场景。加粗请用工具栏，不要输入星号。" />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={12} sm={6}>
            <Form.Item label="分类" name="category" rules={[{ required: true, message: '请选择主题分类' }]}>
              <Select placeholder="选择分类" options={CATEGORY_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item label="CEFR 等级" name="cefr_level" rules={[{ required: true, message: '请选择 CEFR 等级' }]}>
              <Select
                aria-label="CEFR 等级"
                placeholder="选择 CEFR 等级"
                options={THEME_CEFR_LEVELS.map((level) => ({ value: level, label: level }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item label="语言" name="locale" rules={[{ required: true, message: '请选择语言' }]}>
              <Select placeholder="选择语言" options={LOCALE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item label="状态" name="is_active" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </Col>
        </Row>

        <Divider titlePlacement="start">展示资产</Divider>
        <p className={styles.hint}>
          封面、列表钩子、角色卡和标签用于学习者列表与主题主页。未配齐也可保存，后台列表不会隐藏不完整主题。
        </p>

        <Form.Item label="封面图">
          {coverUrl && (
            <div className={styles.coverPreview}>
              <img src={resolveThemeAssetUrl(coverUrl)} alt="封面预览" />
              <Button size="small" danger onClick={() => setCoverUrl('')}>
                移除封面
              </Button>
            </div>
          )}
          <Upload showUploadList={false} accept={IMAGE_ACCEPT} beforeUpload={uploadCover} disabled={uploadingCover}>
            <Button icon={<UploadOutlined />} loading={uploadingCover}>
              {coverUrl ? '更换封面' : '上传封面'}
            </Button>
          </Upload>
          <div className={styles.tip}>支持 jpg / png / gif，先上传拿到 URL，再随主题保存。</div>
        </Form.Item>

        <Form.Item label="列表钩子" name="list_hook">
          <Input.TextArea rows={2} maxLength={200} showCount placeholder="列表横卡上展示的一句钩子，不要截取主题简介" />
        </Form.Item>

        <Form.Item label="标签" name="tags" extra="标签写入 themes_info.tags，不是封面或角色卡字段。">
          <Select mode="tags" placeholder="输入标签后回车，供主题主页展示" tokenSeparators={[',']} />
        </Form.Item>

        <Form.Item label="角色卡" extra="每张卡包含头像、名字和身份。角色卡只给主页展示，不从初始固定设定推导。">
          <div className={styles.cardList}>
            {cards.map((card, index) => (
              <div key={card.uid} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span>角色 {index + 1}</span>
                  <Space>
                    <Button size="small" disabled={index === 0} onClick={() => moveCard(index, -1)}>
                      上移
                    </Button>
                    <Button size="small" disabled={index === cards.length - 1} onClick={() => moveCard(index, 1)}>
                      下移
                    </Button>
                    <Button
                      size="small"
                      danger
                      onClick={() => removeCard(card.uid)}
                    >
                      删除
                    </Button>
                  </Space>
                </div>
                <Row gutter={16}>
                  <Col span={8}>
                    <div className={styles.avatar}>
                      {card.avatar_url && <img src={resolveThemeAssetUrl(card.avatar_url)} alt="角色头像" />}
                      <Upload
                        showUploadList={false}
                        accept={IMAGE_ACCEPT}
                        beforeUpload={uploadAvatar(card.uid)}
                        disabled={uploadingCardUids.has(card.uid)}
                      >
                        <Button size="small" icon={<UploadOutlined />} loading={uploadingCardUids.has(card.uid)}>
                          {card.avatar_url ? '更换头像' : '上传头像'}
                        </Button>
                      </Upload>
                      {card.avatar_url && (
                        <Button size="small" type="link" onClick={() => updateCard(card.uid, { avatar_url: '' })}>
                          移除头像
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col span={8}>
                    <Input
                      value={card.name}
                      placeholder="角色名字"
                      onChange={(event) => updateCard(card.uid, { name: event.target.value })}
                    />
                  </Col>
                  <Col span={8}>
                    <Input
                      value={card.role}
                      placeholder="身份，例如：主角金毛"
                      onChange={(event) => updateCard(card.uid, { role: event.target.value })}
                    />
                  </Col>
                </Row>
              </div>
            ))}
            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              onClick={() => setCards((current) => [...current, { uid: makeCardUid(), avatar_url: '', name: '', role: '' }])}
            >
              添加角色卡
            </Button>
          </div>
        </Form.Item>

        <Form.Item label="初始固定设定" required>
          <CodeEditor
            value={fixedSettingJson}
            onChange={setFixedSettingJson}
            height={200}
            placeholder="请输入JSON格式的固定设定"
          />
        </Form.Item>

        <Form.Item label="第一篇标题" name="first_episode_title">
          <Input
            placeholder="例如: Max's Morning Adventure - Chapter 1: A New Day Begins"
            onBlur={stripOnBlur('first_episode_title')}
          />
        </Form.Item>

        <Form.Item label="第一篇预告内容" name="first_episode_story_text">
          <MarkdownVisualEditor placeholder="第一篇文章的预告内容。加粗请用工具栏，不要输入星号。" />
        </Form.Item>

        <Form.Item label="风格提示">
          <CodeEditor value={styleHintsJson} onChange={setStyleHintsJson} height={120} placeholder="请输入JSON格式的风格提示" />
        </Form.Item>

        <Form.Item
          label="主题专属 Prompt 提示词"
          name="theme_specific_prompt"
          extra="💡 提示：此字段支持 Markdown 格式，可以包含超过 1000 个单词的详细提示词"
        >
          <Input.TextArea
            rows={8}
            showCount
            maxLength={10000}
            placeholder="输入主题专属的Prompt提示词，支持Markdown格式。此提示词将在AI生成篇章时作为额外指导。"
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

interface ThemeModalProps {
  open: boolean
  // 为 null 时新建，否则编辑该主题
  theme: Theme | null
  onClose: () => void
}

// 每次打开都重新挂载表单，从当前主题初始化，关闭后状态不残留
export function ThemeModal({ open, theme, onClose }: Readonly<ThemeModalProps>) {
  if (!open) return null
  return <ThemeForm key={theme?.id ?? 'new'} theme={theme} onClose={onClose} />
}
